import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import CardClient from '@atlaskit/link-provider/client';
import { SmartCardProvider } from '@atlaskit/link-provider/smart-card-provider';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { ProformaIntlProvider } from '@atlassian/proforma-utilities/intl-provider/main';

import {
	buildDefaultFormFieldValidators,
	buildSecurityCriticalValidators,
} from '../../common/utils/validators/constants';
import { useFormRendererActions } from '../../controllers/form-renderer-store';
import lazyMessages from '../../i18n/lazy';
import { FormRenderSuccess } from './form-render-success';
import { FormSections } from './form-sections';
import type { FormRendererProps } from './types';

const formContainerStyles = xcss({
	height: 'auto',
	overflow: 'visible',
	padding: 'space.100',
	backgroundColor: 'color.background.neutral.subtle',
});

const environment = window.location.hostname === 'localhost' ? 'staging' : undefined; // Make smart links connect to staging when developing locally.
const smartCardClient = new CardClient(environment);

const FormRendererInner = ({
	asyncFields = {},
	form,
	lifecycleHandlers = {},
	renderers = {},
	renderMode,
	fieldVisibility,
	fieldValidation,
	attachmentConfiguration,
}: FormRendererProps) => {
	di(FormSections, useFormRendererActions);
	const intl = useIntl();

	const { setFormRendererProps } = useFormRendererActions();
	const defaultFormFieldValidators = useMemo(() => buildDefaultFormFieldValidators(intl), [intl]);
	const securityCriticalFormFieldValidators = useMemo(
		() => buildSecurityCriticalValidators(intl),
		[intl],
	);

	useEffect(() => {
		setFormRendererProps(
			asyncFields,
			form,
			lifecycleHandlers,
			renderers,
			renderMode,
			fieldVisibility,
			fieldValidation,
			defaultFormFieldValidators,
			attachmentConfiguration,
			securityCriticalFormFieldValidators,
		);
	}, [
		asyncFields,
		defaultFormFieldValidators,
		securityCriticalFormFieldValidators,
		form,
		setFormRendererProps,
		lifecycleHandlers,
		renderers,
		renderMode,
		fieldVisibility,
		fieldValidation,
		attachmentConfiguration,
	]);

	return (
		<Box xcss={formContainerStyles} testId="proforma-form-renderer">
			<FormSections />
			<FormRenderSuccess lifecycleHandlers={lifecycleHandlers} />
		</Box>
	);
};

export const FormRenderer = ({
	asyncFields = {},
	form,
	lifecycleHandlers = {},
	renderers = {},
	locale,
	renderMode,
	fieldVisibility,
	fieldValidation,
	attachmentConfiguration,
}: FormRendererProps): React.JSX.Element => {
	di(ProformaIntlProvider);

	return (
		<ProformaIntlProvider messages={lazyMessages} locale={locale}>
			<SmartCardProvider client={smartCardClient}>
				<FormRendererInner
					asyncFields={asyncFields}
					form={form}
					lifecycleHandlers={lifecycleHandlers}
					renderers={renderers}
					locale={locale}
					renderMode={renderMode}
					fieldVisibility={fieldVisibility}
					fieldValidation={fieldValidation}
					attachmentConfiguration={attachmentConfiguration}
				/>
			</SmartCardProvider>
		</ProformaIntlProvider>
	);
};
